
import { defineComponent, inject } from 'vue'
import CustomerNoteTile from '@/components/elements/CustomerNoteTile.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { CustomerOrderService } from '@/services/customerOrder.service'
import { CustomerOrderDetails } from '@/@types/responses/customer_order/CustomerOrderDetails'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'MyOrder',
  components: {
    CustomerNoteTile,
    DotsSvg,
    LoadingPulse,
    NumericInput,
    TextInput
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerOrderService = inject(PROVIDER_CONSTANTS.customerOrderService) as CustomerOrderService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerOrderService,
      stateService,
      toast
    }
  },
  data() {
    let order: CustomerOrderDetails|undefined

    return {
      loading: true,
      order,
      showMenu: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
    }
  },
  computed: {
    stateDisplayValue(): string|undefined {
      if (this.order && this.order.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.order?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getOrder()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getOrder(): void {
      this.customerOrderService.getCustomerOrder(this.id).then(order => {
        this.order = order
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your order. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onEditAccountInformationClicked(): void {
      this.$router.push('/my-account')
    },
    orderPartStatusToDisplayValue(status: number): string {
      switch(status) {
        case 1: return 'Needs Allocated'
        case 2: return 'Ordered'
        case 3: return 'In Stock'
        default: return ''
      }
    },
    statusText(status: number): string {
      switch(status) {
        case 1: return 'Pending'
        case 2: return 'In Progress'
        case 3: return 'Completed'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.order)
        document.title = `Order #${this.order.id} - Short Action Customs, LLC`
    }
  }
})
